import { type LucideIcon } from 'lucide-react';
import {
  ClipboardList,
  Brain,
  Target,
  PenLine,
  SquareGantt,
  Glasses,
  MonitorPlay,
  FileText,
  GraduationCap,
  Dumbbell,
  Library,
  Users2,
  LayoutDashboard,
  Users,
  PenSquare,
  BarChart3,
  Shield,
  Settings,
  Wrench,
  BookOpen,
  Scale,
  Timer,
  Layers,
  Flag,
  MessageCircle,
  MessageSquare,
  LifeBuoy,
} from 'lucide-react';

export type NavVisibility = 'all' | 'staff' | 'admin';

export interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  isActive: (pathname: string) => boolean;
  onHoverPrefetch?: () => void;
  devOnly?: boolean;
  visibility?: NavVisibility;
}

export interface NavGroup {
  id: string;
  label: string;
  icon: LucideIcon;
  items: NavItem[];
  /** If set, clicking the group label navigates here instead of toggling collapse. */
  href?: string;
  isActive?: (pathname: string) => boolean;
  /** Show a small activity dot next to the group label (e.g., unread notifications). */
  showDot?: boolean;
}

export type NavEntry =
  | { type: 'item'; item: NavItem }
  | { type: 'group'; group: NavGroup }
  | { type: 'separator' };

/** Action Plan is rendered separately at the top, outside the scrollable nav entries. */
export function getActionPlanItem(): NavItem {
  return {
    name: 'Action Plan',
    href: '/dashboard',
    icon: ClipboardList,
    isActive: (p) => p.startsWith('/dashboard'),
  };
}

/** Returns the ordered nav entries rendered below Ask JDS. */
export function getNavigationEntries(options: {
  isAskJDS: boolean;
  isAdmin?: boolean;
  prefetchIssueSpotter?: () => void;
}): NavEntry[] {
  return [
    {
      type: 'item',
      item: {
        name: 'Notes',
        href: '/notes',
        icon: FileText,
        isActive: (p) => p.startsWith('/notes'),
      },
    },
    {
      type: 'group',
      group: {
        id: 'practice',
        label: 'Practice',
        icon: Dumbbell,
        items: [
          {
            name: 'Study',
            href: '/flashcards/study',
            icon: Brain,
            isActive: (p) => p.startsWith('/flashcards'),
          },
          {
            name: 'Issue Spotter',
            href: '/issue-spotter',
            icon: Target,
            isActive: (p) => p.startsWith('/issue-spotter'),
            onHoverPrefetch: options.prefetchIssueSpotter,
          },
          {
            name: 'Essay Coach',
            href: '/essay-coach',
            icon: PenLine,
            isActive: (p) => p.startsWith('/essay-coach'),
          },
        ],
      },
    },
    {
      type: 'group',
      group: {
        id: 'library',
        label: 'Library',
        icon: Library,
        items: [
          {
            name: 'Outlines',
            href: '/outlines/library',
            icon: SquareGantt,
            isActive: (p) => p.startsWith('/outlines'),
          },
          {
            name: 'Case Law',
            href: '/cases',
            icon: Glasses,
            isActive: (p) => p.startsWith('/cases'),
          },
          {
            name: 'Courses',
            href: '/courses',
            icon: MonitorPlay,
            isActive: (p) => p === '/courses' || p.startsWith('/course/'),
          },
        ],
      },
    },
    ...(options.isAskJDS || options.isAdmin
      ? [
          {
            type: 'group' as const,
            group: {
              id: 'community',
              label: 'Community',
              icon: Users2,
              href: '/community',
              isActive: (p: string) => p.startsWith('/community'),
              items: [
                {
                  name: 'Discussions',
                  href: '/community',
                  icon: MessageCircle,
                  isActive: (p: string) => p.startsWith('/community') && !p.startsWith('/community/groups') && !p.startsWith('/community/support'),
                },
                {
                  name: 'Study Groups',
                  href: '/community/groups',
                  icon: Users,
                  isActive: (p: string) => p.startsWith('/community/groups'),
                  devOnly: true,
                },
                {
                  name: 'Support',
                  href: '/community/support',
                  icon: MessageSquare,
                  isActive: (p: string) => p.startsWith('/community/support'),
                  devOnly: true,
                },
              ],
            },
          },
        ]
      : []),
    ...(options.isAskJDS || options.isAdmin
      ? [
          {
            type: 'group' as const,
            group: {
              id: 'nextgen',
              label: 'NextGen Bar Prep',
              icon: GraduationCap,
              href: '/nextgen/dashboard',
              isActive: (p: string) => p.startsWith('/nextgen'),
              items: [
                {
                  name: 'Coursework',
                  href: '/nextgen/coursework',
                  icon: BookOpen,
                  isActive: (p: string) => p.startsWith('/nextgen/coursework'),
                  devOnly: true,
                },
                {
                  name: 'Skills',
                  href: '/nextgen/skills',
                  icon: Target,
                  isActive: (p: string) => p.startsWith('/nextgen/skills'),
                  devOnly: true,
                },
                {
                  name: 'Simulations',
                  href: '/nextgen/simulations',
                  icon: Timer,
                  isActive: (p: string) => p.startsWith('/nextgen/simulations'),
                  devOnly: true,
                },
                {
                  name: 'Coach',
                  href: '/nextgen/coach',
                  icon: MessageCircle,
                  isActive: (p: string) => p.startsWith('/nextgen/coach'),
                  devOnly: true,
                },
              ],
            },
          },
        ]
      : []),
  ];
}

/** Returns admin-specific nav entries (shown only on admin domain). */
export function getAdminNavigationEntries(options: {
  isAdmin: boolean;
  isStaff: boolean;
}): NavEntry[] {
  const items: NavItem[] = [
    {
      name: 'Admin Dashboard',
      href: '/admin/dashboard',
      icon: LayoutDashboard,
      isActive: (p) => p === '/admin/dashboard',
      visibility: 'all',
    },
    {
      name: 'Courses',
      href: '/admin/courses',
      icon: BookOpen,
      isActive: (p) => p.startsWith('/admin/courses'),
      visibility: 'all',
    },
    {
      name: 'Flashcards',
      href: '/admin/flashcards',
      icon: Library,
      isActive: (p) => p.startsWith('/admin/flashcards'),
      visibility: 'staff',
    },
    {
      name: 'Outlines',
      href: '/admin/outlines',
      icon: FileText,
      isActive: (p) => p.startsWith('/admin/outlines'),
      visibility: 'staff',
    },
    {
      name: 'Case Law',
      href: '/admin/cases',
      icon: Scale,
      isActive: (p) => p.startsWith('/admin/cases'),
      visibility: 'staff',
    },
    {
      name: 'Blog Management',
      href: '/admin/blog',
      icon: PenSquare,
      isActive: (p) => p.startsWith('/admin/blog'),
      visibility: 'staff',
    },
    {
      name: 'Users',
      href: '/admin/users',
      icon: Users,
      isActive: (p) => p.startsWith('/admin/users'),
      visibility: 'admin',
    },
    {
      name: 'Web Analytics',
      href: '/admin/analytics',
      icon: BarChart3,
      isActive: (p) => p === '/admin/analytics',
      visibility: 'admin',
    },
    {
      name: 'Error Logs',
      href: '/admin/error-logs',
      icon: Shield,
      isActive: (p) => p === '/admin/error-logs',
      visibility: 'admin',
    },
    {
      name: 'Content Health',
      href: '/admin/content-health',
      icon: Layers,
      isActive: (p) => p === '/admin/content-health',
      visibility: 'admin',
    },
    {
      name: 'Moderation',
      href: '/admin/moderation',
      icon: Flag,
      isActive: (p) => p === '/admin/moderation',
      visibility: 'staff',
    },
    {
      name: 'Support',
      href: '/admin/support',
      icon: LifeBuoy,
      isActive: (p) => p.startsWith('/admin/support'),
      visibility: 'staff',
    },
    {
      name: 'Settings',
      href: '/admin/settings',
      icon: Settings,
      isActive: (p) => p === '/admin/settings',
      visibility: 'admin',
    },
  ];

  // Filter by role visibility
  const filtered = items.filter((item) => {
    const vis = item.visibility ?? 'all';
    if (vis === 'all') return true;
    if (vis === 'staff') return options.isStaff;
    if (vis === 'admin') return options.isAdmin;
    return false;
  });

  return [
    {
      type: 'group',
      group: {
        id: 'admin',
        label: 'Admin',
        icon: Wrench,
        items: filtered,
      },
    },
  ];
}
